import * as MetaboxModule from "https://cdn.jsdelivr.net/npm/@3dsource/metabox-modular-configurator-api@3.0.28/+esm";

const {
	integrateMetabox,
	ApplyZoom,
	GetCamera,
	GetScreenshot,
	ResetCamera,
	SetComponent,
	SetCamera,
	SetComponentMaterial,
	SetEnvironment,
	SetEnvironmentMaterial,
	ShowEmbeddedMenu,
} = MetaboxModule;

const debugLog = () => {};
const debugWarn = () => {};
const debugError = () => {};

const SLOT_SEPARATOR = "::";
const PRELOAD_HOST_ID = "metaboxPreloadHost";
const DEFAULT_CAMERA_ANIMATION_DURATION = 1000;
let activeComponentId = null;
let hasAppliedInitialBaseModel = false;
let activeCameraAnimationToken = 0;
let activeCameraAnimationFrameId = null;
let metaboxPerformanceTracker = null;

window.metaboxConfigurator = window.metaboxConfigurator || {};
Object.assign(window.metaboxConfigurator, {
	api: null,
	slots: [],
	environments: [],
	isReady: false,
	changeMaterial: null,
	selectComponent: null,
	changeEnvironment: null,
	changeEnvironmentMaterial: null,
	applyEnvironmentOptions: null,
	reset: null,
	applyZoom: null,
	setCamera: null,
	animateCameraTo: null,
	requestCamera: null,
	requestCameraAndCapture: null,
	captureScreenshot: null,
	lastCameraPayload: null,
	activeEnvironmentId: null,
});

function parseMetaboxConfig() {
	const configElement = document.querySelector(
		"[data-metabox-modular-config]",
	);
	if (!configElement) return {};

	try {
		return JSON.parse(configElement.textContent || "{}");
	} catch (error) {
		console.error("[Metabox] Failed to parse modular config", error);
		return {};
	}
}

const metaboxConfig = parseMetaboxConfig();
const METABOX_BASE_MODEL = metaboxConfig.baseModel || "";
const METABOX_LOADING_IMAGE_URL = metaboxConfig.loadingImageUrl || "";

window.metaboxConfigurator.baseModel = METABOX_BASE_MODEL;

window.__lastMetaboxEnvelope = window.__lastMetaboxEnvelope || null;

function isMetaboxAuditEnabled() {
	return (
		new URLSearchParams(window.location.search).get("metabox_audit") ===
		"1"
	);
}

if (isMetaboxAuditEnabled()) {
	import("./metabox-performance-stats.js")
		.then((module) => {
			if (
				module &&
				typeof module.createMetaboxPerformanceTracker === "function"
			) {
				metaboxPerformanceTracker =
					module.createMetaboxPerformanceTracker();
				if (
					window.metaboxConfigurator &&
					window.metaboxConfigurator.api &&
					typeof metaboxPerformanceTracker.bindApi === "function"
				) {
					metaboxPerformanceTracker.bindApi(
						window.metaboxConfigurator.api,
					);
				}
			}
		})
		.catch(() => {});
}

function ensurePreloadHost(embedElementId) {
	let preloadHost = document.getElementById(PRELOAD_HOST_ID);
	if (preloadHost) return preloadHost;

	const visibleMount = document.getElementById(embedElementId);
	const mountRoot =
		visibleMount?.closest(".product-gallery")?.querySelector(
			".product-gallery__image-list",
		) ||
		visibleMount?.parentElement ||
		document.body;
	if (!(mountRoot instanceof HTMLElement)) return null;

	preloadHost = document.createElement("div");
	preloadHost.id = PRELOAD_HOST_ID;
	preloadHost.className = "metabox-preload-host is-warmup";
	preloadHost.setAttribute("aria-hidden", "true");
	mountRoot.appendChild(preloadHost);

	return preloadHost;
}

function setPreloadHostMode(preloadHost, mode) {
	if (!(preloadHost instanceof HTMLElement)) return;

	preloadHost.classList.toggle("is-active", mode === "active");
	preloadHost.classList.toggle("is-warmup", mode === "warmup");
	preloadHost.style.pointerEvents = mode === "active" ? "auto" : "none";
}

function attachFullscreenToggleToPreloadHost(preloadHost, visibleMount) {
	if (
		!(preloadHost instanceof HTMLElement) ||
		!(visibleMount instanceof HTMLElement)
	) {
		return;
	}

	const fullscreenToggle = visibleMount
		.closest(".product-gallery")
		?.querySelector("[data-configure-fullscreen-toggle]");
	if (!(fullscreenToggle instanceof HTMLElement)) return;
	if (fullscreenToggle.parentElement === preloadHost) return;

	preloadHost.appendChild(fullscreenToggle);
}

function bindPreloadHostOverlay(embedElementId) {
	const visibleMount = document.getElementById(embedElementId);
	if (!(visibleMount instanceof HTMLElement)) return;
	const preloadHost = ensurePreloadHost(embedElementId);
	if (!(preloadHost instanceof HTMLElement)) return;
	attachFullscreenToggleToPreloadHost(preloadHost, visibleMount);

	const carousel = visibleMount
		.closest(".product-gallery")
		?.querySelector(".product-gallery__carousel");
	if (!(carousel instanceof HTMLElement)) return;

	const syncPreloadHostToCarouselSelection = () => {
		const settledCell =
			carousel.selectedCell ||
			(typeof carousel.selectedIndex === "number"
				? carousel.cells?.[carousel.selectedIndex]
				: null);
		if (!(settledCell instanceof HTMLElement)) return;

		const nextMode =
			settledCell.getAttribute("data-media-type") === "configurator"
				? "active"
				: "warmup";
		setPreloadHostMode(preloadHost, nextMode);
	};

	const syncPreloadHostToCell = (cell) => {
		if (!(cell instanceof HTMLElement)) return;

		const nextMode =
			cell.getAttribute("data-media-type") === "configurator"
				? "active"
				: "warmup";
		setPreloadHostMode(preloadHost, nextMode);
	};

	window.addEventListener("metabox:overlay-hide-requested", () => {
		setPreloadHostMode(preloadHost, "warmup");
	});

	window.addEventListener("metabox:overlay-show-requested", () => {
		setPreloadHostMode(preloadHost, "active");
		requestAnimationFrame(() => {
			syncPreloadHostToCarouselSelection();
			requestAnimationFrame(syncPreloadHostToCarouselSelection);
		});
	});

	carousel.addEventListener("carousel:settle", (event) => {
		const settledCell = event.detail?.cell;
		syncPreloadHostToCell(settledCell);
	});

	carousel.addEventListener("carousel:change", (event) => {
		const nextCell = event.detail?.cell;
		if (!(nextCell instanceof HTMLElement)) return;
		if (nextCell.getAttribute("data-media-type") === "configurator") return;

		setPreloadHostMode(preloadHost, "warmup");
	});

	syncPreloadHostToCarouselSelection();
}

function getCommandConstructor(commandNames) {
	const names = Array.isArray(commandNames) ? commandNames : [commandNames];
	for (const commandName of names) {
		if (typeof MetaboxModule[commandName] === "function") {
			return {
				commandName,
				commandConstructor: MetaboxModule[commandName],
			};
		}
	}
	return null;
}

function sendCommandByName(commandNames, argumentVariants) {
	if (!window.metaboxConfigurator.api) {
		debugError("sendCommandByName: API not ready", { commandNames });
		return false;
	}

	const commandInfo = getCommandConstructor(commandNames);
	if (!commandInfo) {
		debugWarn("sendCommandByName: command constructor not found", {
			commandNames,
		});
		return false;
	}

	const variants =
		Array.isArray(argumentVariants) && argumentVariants.length
			? argumentVariants
			: [[]];
	for (const args of variants) {
		try {
			const normalizedArgs = Array.isArray(args) ? args : [args];
			const command = new commandInfo.commandConstructor(
				...normalizedArgs,
			);
			window.metaboxConfigurator.api.sendCommandToMetabox(command);
			debugLog("sendCommandByName: command sent", {
				commandName: commandInfo.commandName,
				args: normalizedArgs,
			});
			return true;
		} catch (error) {
			debugWarn("sendCommandByName: failed signature variant", {
				commandName: commandInfo.commandName,
				args,
				error: String(error),
			});
		}
	}

	return false;
}

function toCompositeSlotId(componentId, slotId) {
	return `${componentId}${SLOT_SEPARATOR}${slotId}`;
}

function parseCompositeSlotId(slotKey) {
	if (!slotKey || typeof slotKey !== "string") return null;
	const separatorIndex = slotKey.indexOf(SLOT_SEPARATOR);
	if (separatorIndex <= 0) return null;

	return {
		componentId: slotKey.slice(0, separatorIndex),
		slotId: slotKey.slice(separatorIndex + SLOT_SEPARATOR.length),
	};
}

function extractComponents(configurator) {
	if (!configurator || typeof configurator !== "object") return [];
	if (Array.isArray(configurator.components)) return configurator.components;
	return [];
}

function normalizeLookupValue(value) {
	if (!value) return "";
	return value
		.toString()
		.trim()
		.toLowerCase()
		.replace(/[_/]+/g, " ")
		.replace(/&/g, " and ")
		.replace(/[^a-z0-9\s-]/g, " ")
		.replace(/\band\b/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function getRootComponents(components) {
	if (!Array.isArray(components)) return [];
	return components.filter((component) =>
		Boolean(component?.componentType?.isRoot),
	);
}

function getComponentLookupValues(component) {
	return [
		component?.id,
		component?.name,
		component?.componentType?.id,
		component?.componentType?.name,
		component?.product?.id,
		component?.product?.title,
		component?.product?.externalId,
	]
		.map((value) => normalizeLookupValue(value))
		.filter(Boolean);
}

function resolveInitialBaseModelComponent(components, rawBaseModel) {
	const normalizedBaseModel = normalizeLookupValue(rawBaseModel);
	if (!normalizedBaseModel) return null;

	const rootComponents = getRootComponents(components);
	if (!rootComponents.length) return null;

	const exactMatch = rootComponents.find((component) =>
		getComponentLookupValues(component).includes(normalizedBaseModel),
	);
	if (exactMatch) return exactMatch;

	return (
		rootComponents.find((component) =>
			getComponentLookupValues(component).some(
				(value) =>
					value.includes(normalizedBaseModel) ||
					normalizedBaseModel.includes(value),
			),
		) || null
	);
}

function normalizeComponentSlots(components) {
	const normalized = [];
	components.forEach((component) => {
		const componentId = component?.id;
		const rawSlots = component?.product?.slots;
		if (!componentId || !Array.isArray(rawSlots)) return;

		rawSlots.forEach((slot) => {
			if (!slot?.id) return;
			normalized.push({
				...slot,
				id: toCompositeSlotId(componentId, slot.id),
				componentId,
				rawSlotId: slot.id,
				label: slot.label || slot.id,
			});
		});
	});
	return normalized;
}

function resolveComponentSlot(slotKey, explicitComponentId) {
	if (explicitComponentId && slotKey) {
		return { componentId: explicitComponentId, slotId: slotKey };
	}

	const parsed = parseCompositeSlotId(slotKey);
	if (parsed) {
		return parsed;
	}

	if (activeComponentId && slotKey) {
		return { componentId: activeComponentId, slotId: slotKey };
	}

	return null;
}

function changeMaterial(slotIdOrComposite, materialId, componentId) {
	if (!window.metaboxConfigurator.api) return false;
	if (!slotIdOrComposite || !materialId) return false;

	const resolved = resolveComponentSlot(slotIdOrComposite, componentId);
	if (!resolved?.componentId || !resolved?.slotId) {
		debugWarn("changeMaterial: could not resolve component slot", {
			slotIdOrComposite,
			componentId,
		});
		return false;
	}

	window.metaboxConfigurator.api.sendCommandToMetabox(
		new SetComponentMaterial(
			resolved.componentId,
			resolved.slotId,
			materialId,
		),
	);
	return true;
}

function getComponentById(componentId) {
	if (!componentId) return null;
	const configurator = window.__lastMetaboxEnvelope?.configurator;
	const components = Array.isArray(configurator?.components)
		? configurator.components
		: [];
	return (
		components.find((component) => component?.id === componentId) || null
	);
}

function selectComponent(componentId, componentTypeId, isRoot) {
	if (!window.metaboxConfigurator.api) return false;
	if (!componentId) return false;

	const component = getComponentById(componentId);
	const resolvedTypeId =
		componentTypeId || component?.componentType?.id || null;
	const resolvedIsRoot =
		typeof isRoot === "boolean"
			? isRoot
			: Boolean(component?.componentType?.isRoot);

	if (!resolvedTypeId) {
		debugWarn("selectComponent: missing componentTypeId", {
			componentId,
			componentTypeId,
		});
		return false;
	}

	try {
		window.metaboxConfigurator.api.sendCommandToMetabox(
			new SetComponent(componentId, resolvedTypeId, resolvedIsRoot),
		);
		activeComponentId = componentId;
		return true;
	} catch (error) {
		debugWarn("selectComponent direct call failed, trying aliases", {
			componentId,
			resolvedTypeId,
			resolvedIsRoot,
			error: String(error),
		});
	}

	const selected = sendCommandByName(
		[
			"SetComponent",
			"SetActiveComponent",
			"SelectComponent",
			"SetConfiguratorComponent",
		],
		[
			[componentId, resolvedTypeId, resolvedIsRoot],
			[
				{
					id: componentId,
					typeId: resolvedTypeId,
					isRoot: resolvedIsRoot,
				},
			],
			[
				{
					componentId,
					componentTypeId: resolvedTypeId,
					isRoot: resolvedIsRoot,
				},
			],
			[
				{
					component: componentId,
					type: resolvedTypeId,
					isRoot: resolvedIsRoot,
				},
			],
			[componentId],
			[{ componentId }],
			[{ id: componentId }],
		],
	);

	if (selected) {
		activeComponentId = componentId;
	}

	return selected;
}

function changeEnvironment(environmentId) {
	if (!window.metaboxConfigurator.api) return false;
	if (!environmentId) return false;

	try {
		window.metaboxConfigurator.api.sendCommandToMetabox(
			new SetEnvironment(environmentId),
		);
		window.metaboxConfigurator.activeEnvironmentId = environmentId;
		return true;
	} catch (error) {
		debugWarn(
			"changeEnvironment direct call failed, trying aliases",
			String(error),
		);
		const changed = sendCommandByName(
			[
				"SetEnvironment",
				"SetEnvironmentById",
				"SetConfiguratorEnvironment",
			],
			[
				[environmentId],
				[{ environmentId }],
				[{ id: environmentId }],
				[{ environment: environmentId }],
			],
		);
		if (changed) {
			window.metaboxConfigurator.activeEnvironmentId = environmentId;
		}
		return changed;
	}
}

function changeEnvironmentMaterial(slotId, materialId) {
	if (!window.metaboxConfigurator.api) return false;
	if (!slotId || !materialId) return false;

	try {
		window.metaboxConfigurator.api.sendCommandToMetabox(
			new SetEnvironmentMaterial(slotId, materialId),
		);
		return true;
	} catch (error) {
		debugWarn(
			"SetEnvironmentMaterial direct call failed, trying aliases",
			String(error),
		);
		return sendCommandByName(
			[
				"SetEnvironmentMaterial",
				"SetEnvironmentSlotMaterial",
				"SetSlotEnvironmentMaterial",
			],
			[
				[slotId, materialId],
				[{ slotId, materialId }],
				[{ slot: slotId, material: materialId }],
			],
		);
	}
}

function applyEnvironmentOptions(options = {}) {
	if (!options || typeof options !== "object") return false;

	let hasAnyAction = false;
	let success = true;

	if (options.environmentId) {
		hasAnyAction = true;
		success = changeEnvironment(options.environmentId) && success;
	}

	if (options.slotMaterials && typeof options.slotMaterials === "object") {
		Object.entries(options.slotMaterials).forEach(
			([slotId, materialId]) => {
				if (!slotId || !materialId) return;
				hasAnyAction = true;
				success =
					changeEnvironmentMaterial(slotId, materialId) && success;
			},
		);
	}

	return hasAnyAction && success;
}

function reset() {
	const slots = window.metaboxConfigurator.slots;
	if (!Array.isArray(slots) || slots.length === 0) return false;

	slots.forEach((slot) => {
		const firstMaterial = slot?.enabledMaterials?.[0];
		if (firstMaterial?.id) {
			changeMaterial(slot.id, firstMaterial.id);
		}
	});

	return true;
}

function applyZoom(zoomValue) {
	if (!window.metaboxConfigurator.api) return false;
	window.metaboxConfigurator.api.sendCommandToMetabox(
		new ApplyZoom(zoomValue),
	);
	return true;
}

function normalizeCameraPayload(cameraPayload) {
	if (!cameraPayload) return null;

	if (typeof cameraPayload === "string") {
		try {
			return JSON.parse(cameraPayload);
		} catch (error) {
			debugWarn("normalizeCameraPayload: invalid JSON string", {
				error: String(error),
			});
			return null;
		}
	}

	return typeof cameraPayload === "object" ? cameraPayload : null;
}

function setCamera(cameraPayload) {
	if (!window.metaboxConfigurator.api) return false;

	const normalizedPayload = normalizeCameraPayload(cameraPayload);
	if (!normalizedPayload) return false;

	try {
		window.metaboxConfigurator.api.sendCommandToMetabox(
			new SetCamera(normalizedPayload),
		);
		window.metaboxConfigurator.lastCameraPayload = normalizedPayload;
		return true;
	} catch (error) {
		debugWarn("SetCamera direct call failed", String(error));
		const applied = sendCommandByName(
			["SetCamera"],
			[
				[normalizedPayload],
				[{ camera: normalizedPayload }],
				[{ payload: normalizedPayload }],
			],
		);
		if (applied) {
			window.metaboxConfigurator.lastCameraPayload = normalizedPayload;
		}
		return applied;
	}
}

function lerpNumber(fromValue, toValue, progress) {
	if (
		typeof fromValue !== "number" ||
		typeof toValue !== "number" ||
		!Number.isFinite(fromValue) ||
		!Number.isFinite(toValue)
	) {
		return toValue;
	}

	return fromValue + (toValue - fromValue) * progress;
}

function cubicBezierAtTime(progress, x1, y1, x2, y2) {
	const sampleCurveX = (time) =>
		((1 - 3 * x2 + 3 * x1) * time + (3 * x2 - 6 * x1)) * time * time +
		3 * x1 * time;
	const sampleCurveY = (time) =>
		((1 - 3 * y2 + 3 * y1) * time + (3 * y2 - 6 * y1)) * time * time +
		3 * y1 * time;
	const sampleCurveDerivativeX = (time) =>
		(3 * (1 - 3 * x2 + 3 * x1) * time + 2 * (3 * x2 - 6 * x1)) * time +
		3 * x1;

	const solveCurveX = (targetX) => {
		let time = targetX;
		for (let index = 0; index < 8; index += 1) {
			const deltaX = sampleCurveX(time) - targetX;
			if (Math.abs(deltaX) < 1e-6) return time;

			const derivative = sampleCurveDerivativeX(time);
			if (Math.abs(derivative) < 1e-6) break;
			time -= deltaX / derivative;
		}

		let lowerBound = 0;
		let upperBound = 1;
		time = targetX;

		while (lowerBound < upperBound) {
			const currentX = sampleCurveX(time);
			if (Math.abs(currentX - targetX) < 1e-6) return time;
			if (targetX > currentX) {
				lowerBound = time;
			} else {
				upperBound = time;
			}
			time = (upperBound - lowerBound) * 0.5 + lowerBound;
			if (Math.abs(upperBound - lowerBound) < 1e-6) break;
		}

		return time;
	};

	if (progress <= 0) return 0;
	if (progress >= 1) return 1;

	return sampleCurveY(solveCurveX(progress));
}

function getCameraAnimationEasing() {
	return (progress) => cubicBezierAtTime(progress, 0.4, 0, 0.22, 1);
}

function buildAnimatedCameraPayload(fromPayload, toPayload, progress) {
	const normalizedFromPayload = normalizeCameraPayload(fromPayload) || {};
	const normalizedToPayload = normalizeCameraPayload(toPayload) || {};

	return {
		...normalizedToPayload,
		mode:
			normalizedToPayload.mode || normalizedFromPayload.mode || "orbit",
		fov:
			typeof normalizedToPayload.fov === "number"
				? lerpNumber(
						normalizedFromPayload.fov,
						normalizedToPayload.fov,
						progress,
					)
				: normalizedFromPayload.fov,
		position:
			normalizedToPayload.position &&
			typeof normalizedToPayload.position === "object"
				? {
						x: lerpNumber(
							normalizedFromPayload.position?.x,
							normalizedToPayload.position.x,
							progress,
						),
						y: lerpNumber(
							normalizedFromPayload.position?.y,
							normalizedToPayload.position.y,
							progress,
						),
						z: lerpNumber(
							normalizedFromPayload.position?.z,
							normalizedToPayload.position.z,
							progress,
						),
					}
				: normalizedFromPayload.position,
		rotation:
			normalizedToPayload.rotation &&
			typeof normalizedToPayload.rotation === "object"
				? {
						horizontal: lerpNumber(
							normalizedFromPayload.rotation?.horizontal,
							normalizedToPayload.rotation.horizontal,
							progress,
						),
						vertical: lerpNumber(
							normalizedFromPayload.rotation?.vertical,
							normalizedToPayload.rotation.vertical,
							progress,
						),
					}
				: normalizedFromPayload.rotation,
	};
}

function getCameraAnimationOptions(options = {}) {
	const duration =
		typeof options?.duration === "number" && options.duration > 0
			? options.duration
			: DEFAULT_CAMERA_ANIMATION_DURATION;
	activeCameraAnimationToken += 1;
	const animationToken = activeCameraAnimationToken;

	if (activeCameraAnimationFrameId) {
		window.cancelAnimationFrame(activeCameraAnimationFrameId);
		activeCameraAnimationFrameId = null;
	}

	return {
		duration,
		easing: getCameraAnimationEasing(),
		animationToken,
		shouldFallbackToImmediate:
			duration <= 0 ||
			typeof window.requestAnimationFrame !== "function",
		captureTimeout:
			typeof options?.captureTimeout === "number" &&
			options.captureTimeout > 0
				? options.captureTimeout
				: 2000,
	};
}

function animateBetweenCameraPayloads(
	fromPayload,
	toPayload,
	animationOptions,
) {
	const normalizedFromPayload = normalizeCameraPayload(fromPayload);
	const normalizedToPayload = normalizeCameraPayload(toPayload);
	if (!normalizedFromPayload || !normalizedToPayload) return false;

	const { duration, easing, animationToken } = animationOptions;

	return new Promise((resolve) => {
		let animationStartTime = null;

		const step = (timestamp) => {
			if (animationToken !== activeCameraAnimationToken) {
				resolve(false);
				return;
			}

			if (animationStartTime === null) {
				animationStartTime = timestamp;
			}

			const elapsed = timestamp - animationStartTime;
			const progress = Math.min(1, elapsed / duration);
			const easedProgress = easing(progress);
			const framePayload = buildAnimatedCameraPayload(
				normalizedFromPayload,
				normalizedToPayload,
				easedProgress,
			);

			setCamera(framePayload);

			if (progress >= 1) {
				activeCameraAnimationFrameId = null;
				resolve(true);
				return;
			}

			activeCameraAnimationFrameId = window.requestAnimationFrame(step);
		};

		activeCameraAnimationFrameId = window.requestAnimationFrame(step);
	});
}

async function animateCameraTo(cameraPayload, options = {}) {
	if (!window.metaboxConfigurator.api) return false;

	const normalizedPayload = normalizeCameraPayload(cameraPayload);
	if (!normalizedPayload) return false;

	const animationOptions = getCameraAnimationOptions(options);
	if (animationOptions.shouldFallbackToImmediate) {
		return setCamera(normalizedPayload);
	}

	let fromPayload = null;
	try {
		fromPayload = await requestCameraAndCapture({
			timeout: animationOptions.captureTimeout,
		});
	} catch (_error) {
		fromPayload = window.metaboxConfigurator.lastCameraPayload || null;
	}

	if (!normalizeCameraPayload(fromPayload)) {
		return setCamera(normalizedPayload);
	}

	return animateBetweenCameraPayloads(
		fromPayload,
		normalizedPayload,
		animationOptions,
	);
}

function requestCamera() {
	if (!window.metaboxConfigurator.api) return false;

	try {
		window.metaboxConfigurator.api.sendCommandToMetabox(new GetCamera());
		return true;
	} catch (error) {
		debugWarn("GetCamera direct call failed", String(error));
		return sendCommandByName(["GetCamera"], [[], [{}]]);
	}
}

function requestCameraAndCapture(options = {}) {
	const timeout =
		typeof options?.timeout === "number" && options.timeout > 0
			? options.timeout
			: 4000;

	return new Promise((resolve, reject) => {
		let timeoutId = null;

		const cleanup = () => {
			window.removeEventListener(
				"metabox:cameraCaptured",
				handleCameraCaptured,
			);
			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}
		};

		const handleCameraCaptured = (event) => {
			cleanup();
			resolve(event.detail?.payload || null);
		};

		window.addEventListener(
			"metabox:cameraCaptured",
			handleCameraCaptured,
			{ once: true },
		);

		const requested = requestCamera();
		if (!requested) {
			cleanup();
			reject(new Error("Metabox camera request failed"));
			return;
		}

		timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error("Timed out waiting for metabox camera payload"));
		}, timeout);
	});
}

function captureScreenshot(mimeType = "image/png", size) {
	if (!window.metaboxConfigurator.api) return false;

	try {
		const command =
			size && typeof size.x === "number" && typeof size.y === "number"
				? new GetScreenshot(mimeType, size)
				: new GetScreenshot(mimeType);
		window.metaboxConfigurator.api.sendCommandToMetabox(command);
		return true;
	} catch (error) {
		debugWarn("GetScreenshot direct call failed", String(error));
		return sendCommandByName(
			["GetScreenshot"],
			[
				[mimeType, size],
				[{ mimeType, size }],
				[{ format: mimeType, size }],
			],
			);
	}
}

function initMetabox() {
	const configuratorId = metaboxConfig.configuratorId || "";
	const embedElementId = metaboxConfig.embedElementId || "embed3DSource";

	debugLog("init:start", { configuratorId, embedElementId });

	if (!configuratorId) {
		debugError("init: missing configuratorId");
		return;
	}

	const embedElement = document.getElementById(embedElementId);
	if (!embedElement) {
		debugError("init: embed element not found", { embedElementId });
		return;
	}

	const preloadHost = ensurePreloadHost(embedElementId);
	if (!(preloadHost instanceof HTMLElement)) return;
	bindPreloadHostOverlay(embedElementId);

	const integrationConfig = METABOX_LOADING_IMAGE_URL
		? {
				loadingImage: METABOX_LOADING_IMAGE_URL,
				introImage: METABOX_LOADING_IMAGE_URL,
			}
		: {};

	integrateMetabox(
		configuratorId,
		preloadHost.id,
		(metaboxApi) => {
			window.metaboxConfigurator.api = metaboxApi;
			window.metaboxConfigurator.changeMaterial = changeMaterial;
			window.metaboxConfigurator.selectComponent = selectComponent;
			window.metaboxConfigurator.changeEnvironment = changeEnvironment;
			window.metaboxConfigurator.changeEnvironmentMaterial =
				changeEnvironmentMaterial;
			window.metaboxConfigurator.applyEnvironmentOptions =
				applyEnvironmentOptions;
			window.metaboxConfigurator.reset = reset;
			window.metaboxConfigurator.applyZoom = applyZoom;
			window.metaboxConfigurator.setCamera = setCamera;
			window.metaboxConfigurator.animateCameraTo = animateCameraTo;
			window.metaboxConfigurator.requestCamera = requestCamera;
			window.metaboxConfigurator.requestCameraAndCapture =
				requestCameraAndCapture;
			window.metaboxConfigurator.captureScreenshot = captureScreenshot;
			window.metaboxConfigurator.isReady = true;
			if (
				metaboxPerformanceTracker &&
				typeof metaboxPerformanceTracker.bindApi === "function"
			) {
				metaboxPerformanceTracker.bindApi(metaboxApi);
			}

			window.dispatchEvent(
				new CustomEvent("metabox:ready", {
					detail: { api: metaboxApi },
				}),
			);

			metaboxApi.addEventListener(
				"getCameraResult",
				(payload) => {
					window.metaboxConfigurator.lastCameraPayload =
						payload && typeof payload === "object" ? payload : null;

					window.dispatchEvent(
						new CustomEvent("metabox:cameraCaptured", {
							detail: {
								payload:
									payload && typeof payload === "object"
										? payload
										: null,
								json:
									payload && typeof payload === "object"
										? JSON.stringify(payload)
										: null,
							},
						}),
					);
				},
			);

			metaboxApi.addEventListener(
				"configuratorDataUpdated",
				(envelope) => {
					window.__lastMetaboxEnvelope = envelope;
					const configurator = envelope?.configurator;
					if (!configurator) {
						debugError(
							"configuratorDataUpdated: missing configurator payload",
							envelope,
						);
						return;
					}

					const environments = Array.isArray(
						configurator.environments,
					)
						? configurator.environments
						: [];
					window.metaboxConfigurator.environments = environments;

					const envelopeEnvironmentId =
						envelope?.environmentId || null;
					if (envelopeEnvironmentId) {
						window.metaboxConfigurator.activeEnvironmentId =
							envelopeEnvironmentId;
					} else if (
						!window.metaboxConfigurator.activeEnvironmentId &&
						environments.length > 0
					) {
						const firstEnvironment = environments[0];
						window.metaboxConfigurator.activeEnvironmentId =
							firstEnvironment.environmentId ||
							firstEnvironment.id ||
							null;
					}

					window.dispatchEvent(
						new CustomEvent("metabox:environmentsReady", {
							detail: {
								environments,
								activeEnvironmentId:
									window.metaboxConfigurator
										.activeEnvironmentId,
							},
						}),
					);

					const components = extractComponents(configurator);
					const configurationTree = Array.isArray(
						envelope?.configurationTree,
					)
						? envelope.configurationTree
						: [];
					const rootNode = configurationTree.find(
						(node) => node && !node.parentId,
					);

					if (!hasAppliedInitialBaseModel) {
						hasAppliedInitialBaseModel = true;

						const requestedBaseModel = METABOX_BASE_MODEL;
						const initialBaseModelComponent =
							resolveInitialBaseModelComponent(
								components,
								requestedBaseModel,
							);

						if (requestedBaseModel && !initialBaseModelComponent) {
							debugWarn(
								"configuratorDataUpdated: initial base model not found, keeping default root",
								{
									requestedBaseModel,
								},
							);
						} else if (
							initialBaseModelComponent &&
							initialBaseModelComponent.id !== rootNode?.id
						) {
							debugLog(
								"configuratorDataUpdated: applying initial base model override",
								{
									requestedBaseModel,
									previousRootId: rootNode?.id || null,
									nextRootId: initialBaseModelComponent.id,
								},
							);

							const selected = selectComponent(
								initialBaseModelComponent.id,
								initialBaseModelComponent.componentType?.id,
								true,
							);

							if (selected) {
								return;
							}

							debugWarn(
								"configuratorDataUpdated: failed to apply initial base model override",
								{
									requestedBaseModel,
									componentId: initialBaseModelComponent.id,
								},
							);
						}
					}

					activeComponentId =
						rootNode?.id || components[0]?.id || activeComponentId;

					const slots = normalizeComponentSlots(components);
					window.metaboxConfigurator.slots = slots;

					if (slots.length > 0) {
						window.dispatchEvent(
							new CustomEvent("metabox:slotsReady", {
								detail: { slots },
							}),
						);
						debugLog("event dispatched: metabox:slotsReady", {
							slotsCount: slots.length,
							componentsCount: components.length,
						});
					} else {
						debugWarn(
							"configuratorDataUpdated: no component slots available",
							{ componentsCount: components.length },
						);
					}
				},
			);
			try {
				metaboxApi.sendCommandToMetabox(new ShowEmbeddedMenu(false));
			} catch (error) {
				debugWarn(
					"ShowEmbeddedMenu direct call failed, trying aliases",
					String(error),
				);
				sendCommandByName(
					["ShowEmbeddedMenu", "SetEmbeddedMenuVisible"],
					[[false], [{ visible: false }], [{ isVisible: false }]],
				);
			}

		},
		integrationConfig,
	);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initMetabox);
} else {
	initMetabox();
}
